import * as THREE from 'three';
import { isBoxWithinFloorBounds } from './meshOperations';
import { ThreeRefs } from './types';

const calculateRotatedBoundingBox = (
  mesh: THREE.Mesh
): {
  min: THREE.Vector3;
  max: THREE.Vector3;
  size: THREE.Vector3;
} => {
  const clonedGeometry = mesh.geometry.clone();

  const matrix = new THREE.Matrix4();
  matrix.makeRotationFromEuler(mesh.rotation);
  clonedGeometry.applyMatrix4(matrix);

  // Compute the bounding box
  clonedGeometry.computeBoundingBox();
  const bbox = clonedGeometry.boundingBox!;

  // Calculate world coordinates
  const min = bbox.min.clone().add(mesh.position);
  const max = bbox.max.clone().add(mesh.position);
  const size = bbox.getSize(new THREE.Vector3());

  // Clean up to avoid memory leaks
  clonedGeometry.dispose();

  return { min, max, size };
};

export const alignHorizontally = (refs: ThreeRefs): void => {
  if (!refs.selectedMeshes.current.length || !refs.floorPlane.current) return;

  const avgZ =
    refs.selectedMeshes.current.reduce(
      (sum, mesh) => sum + mesh.position.z,
      0
    ) / refs.selectedMeshes.current.length;

  refs.selectedMeshes.current.forEach((mesh) => {
    const newPosition = mesh.position.clone();
    newPosition.z = avgZ;

    const geometry = mesh.geometry as THREE.PlaneGeometry;

    if (
      isBoxWithinFloorBounds(
        newPosition,
        geometry.parameters.width,
        geometry.parameters.height,
        refs.floorPlane.current!
      )
    ) {
      mesh.position.copy(newPosition);
      updateOverlayPosition(mesh, refs.scene.current, newPosition);
    }
  });
};

export const alignVertically = (refs: ThreeRefs): void => {
  if (!refs.selectedMeshes.current.length || !refs.floorPlane.current) return;

  const avgX =
    refs.selectedMeshes.current.reduce(
      (sum, mesh) => sum + mesh.position.x,
      0
    ) / refs.selectedMeshes.current.length;

  refs.selectedMeshes.current.forEach((mesh) => {
    const newPosition = mesh.position.clone();
    newPosition.x = avgX;

    const geometry = mesh.geometry as THREE.PlaneGeometry;

    if (
      isBoxWithinFloorBounds(
        newPosition,
        geometry.parameters.width,
        geometry.parameters.height,
        refs.floorPlane.current!
      )
    ) {
      mesh.position.copy(newPosition);
      updateOverlayPosition(mesh, refs.scene.current, newPosition);
    }
  });
};

export const alignLeft = (refs: ThreeRefs): void => {
  if (!refs.selectedMeshes.current.length || !refs.floorPlane.current) return;

  // Calculate the leftmost point considering rotation
  const leftMost = Math.min(
    ...refs.selectedMeshes.current.map((mesh) => {
      const bbox = calculateRotatedBoundingBox(mesh);
      return bbox.min.x;
    })
  );

  // Align each mesh to the leftmost point
  refs.selectedMeshes.current.forEach((mesh) => {
    const bbox = calculateRotatedBoundingBox(mesh);
    // Calculate how much to move the mesh so its left edge aligns with leftMost
    const offsetX = leftMost - bbox.min.x;

    const newPosition = mesh.position.clone();
    newPosition.x += offsetX;

    const geometry = mesh.geometry as THREE.PlaneGeometry;
    if (
      isBoxWithinFloorBounds(
        newPosition,
        geometry.parameters.width,
        geometry.parameters.height,
        refs.floorPlane.current!
      )
    ) {
      mesh.position.copy(newPosition);
      updateOverlayPosition(mesh, refs.scene.current, newPosition);
    }
  });
};

export const alignRight = (refs: ThreeRefs): void => {
  if (!refs.selectedMeshes.current.length || !refs.floorPlane.current) return;

  // Calculate the rightmost point considering rotation
  const rightMost = Math.max(
    ...refs.selectedMeshes.current.map((mesh) => {
      const bbox = calculateRotatedBoundingBox(mesh);
      return bbox.max.x;
    })
  );

  // Align each mesh to the rightmost point
  refs.selectedMeshes.current.forEach((mesh) => {
    const bbox = calculateRotatedBoundingBox(mesh);
    // Calculate how much to move the mesh so its right edge aligns with rightMost
    const offsetX = rightMost - bbox.max.x;

    const newPosition = mesh.position.clone();
    newPosition.x += offsetX;

    const geometry = mesh.geometry as THREE.PlaneGeometry;
    if (
      isBoxWithinFloorBounds(
        newPosition,
        geometry.parameters.width,
        geometry.parameters.height,
        refs.floorPlane.current!
      )
    ) {
      mesh.position.copy(newPosition);
      updateOverlayPosition(mesh, refs.scene.current, newPosition);
    }
  });
};

export const alignTop = (refs: ThreeRefs): void => {
  if (!refs.selectedMeshes.current.length || !refs.floorPlane.current) return;

  // Calculate the topmost point considering rotation (minimum Z in Three.js coordinates)
  const topMost = Math.min(
    ...refs.selectedMeshes.current.map((mesh) => {
      const bbox = calculateRotatedBoundingBox(mesh);
      return bbox.min.z;
    })
  );

  // Align each mesh to the topmost point
  refs.selectedMeshes.current.forEach((mesh) => {
    const bbox = calculateRotatedBoundingBox(mesh);
    // Calculate how much to move the mesh so its top edge aligns with topMost
    const offsetZ = topMost - bbox.min.z;

    const newPosition = mesh.position.clone();
    newPosition.z += offsetZ;

    const geometry = mesh.geometry as THREE.PlaneGeometry;
    if (
      isBoxWithinFloorBounds(
        newPosition,
        geometry.parameters.width,
        geometry.parameters.height,
        refs.floorPlane.current!
      )
    ) {
      mesh.position.copy(newPosition);
      updateOverlayPosition(mesh, refs.scene.current, newPosition);
    }
  });
};

export const alignBottom = (refs: ThreeRefs): void => {
  if (!refs.selectedMeshes.current.length || !refs.floorPlane.current) return;

  // Calculate the bottommost point considering rotation (maximum Z in Three.js coordinates)
  const bottomMost = Math.max(
    ...refs.selectedMeshes.current.map((mesh) => {
      const bbox = calculateRotatedBoundingBox(mesh);
      return bbox.max.z;
    })
  );

  // Align each mesh to the bottommost point
  refs.selectedMeshes.current.forEach((mesh) => {
    const bbox = calculateRotatedBoundingBox(mesh);
    // Calculate how much to move the mesh so its bottom edge aligns with bottomMost
    const offsetZ = bottomMost - bbox.max.z;

    const newPosition = mesh.position.clone();
    newPosition.z += offsetZ;

    const geometry = mesh.geometry as THREE.PlaneGeometry;
    if (
      isBoxWithinFloorBounds(
        newPosition,
        geometry.parameters.width,
        geometry.parameters.height,
        refs.floorPlane.current!
      )
    ) {
      mesh.position.copy(newPosition);
      updateOverlayPosition(mesh, refs.scene.current, newPosition);
    }
  });
};

export const alignAdjacent = (refs: ThreeRefs): void => {
  if (!refs.selectedMeshes.current.length || !refs.floorPlane.current) return;

  // Sort meshes by x position to maintain left-to-right order
  const sortedMeshes = [...refs.selectedMeshes.current].sort(
    (a, b) => a.position.x - b.position.x
  );

  // Start with the leftmost mesh as reference
  for (let i = 1; i < sortedMeshes.length; i++) {
    const prevMesh = sortedMeshes[i - 1];
    const currentMesh = sortedMeshes[i];

    // Calculate bounding boxes considering rotation
    const prevBBox = calculateRotatedBoundingBox(prevMesh);
    const currentBBox = calculateRotatedBoundingBox(currentMesh);

    // Calculate how much to move the current mesh
    const offsetX = prevBBox.max.x - currentBBox.min.x;

    const newPosition = currentMesh.position.clone();
    newPosition.x += offsetX;

    const geometry = currentMesh.geometry as THREE.PlaneGeometry;
    if (
      isBoxWithinFloorBounds(
        newPosition,
        geometry.parameters.width,
        geometry.parameters.height,
        refs.floorPlane.current!
      )
    ) {
      currentMesh.position.copy(newPosition);
      updateOverlayPosition(currentMesh, refs.scene.current, newPosition);
    }
  }
};

export const alignAdjacentVertical = (refs: ThreeRefs): void => {
  if (!refs.selectedMeshes.current.length || !refs.floorPlane.current) return;

  // Sort meshes by z position to maintain top-to-bottom order
  const sortedMeshes = [...refs.selectedMeshes.current].sort(
    (a, b) => a.position.z - b.position.z
  );

  // Start with the topmost mesh as reference
  for (let i = 1; i < sortedMeshes.length; i++) {
    const prevMesh = sortedMeshes[i - 1];
    const currentMesh = sortedMeshes[i];

    // Calculate bounding boxes considering rotation
    const prevBBox = calculateRotatedBoundingBox(prevMesh);
    const currentBBox = calculateRotatedBoundingBox(currentMesh);

    // Calculate how much to move the current mesh
    const offsetZ = prevBBox.max.z - currentBBox.min.z;

    const newPosition = currentMesh.position.clone();
    newPosition.z += offsetZ;

    const geometry = currentMesh.geometry as THREE.PlaneGeometry;
    if (
      isBoxWithinFloorBounds(
        newPosition,
        geometry.parameters.width,
        geometry.parameters.height,
        refs.floorPlane.current!
      )
    ) {
      currentMesh.position.copy(newPosition);
      updateOverlayPosition(currentMesh, refs.scene.current, newPosition);
    }
  }
};

const updateOverlayPosition = (
  mesh: THREE.Mesh,
  scene: THREE.Scene | null,
  newPosition: THREE.Vector3
): void => {
  if (!scene) return;
  if (mesh.userData.hasOverlay) {
    const overlayUuid = mesh.userData.hasOverlay;
    const overlay = scene.children.find((obj) => obj.uuid === overlayUuid);
    if (overlay) {
      overlay.position.copy(newPosition);
      overlay.position.y += 0.001;
    }
  }

  if (mesh.userData.isQueueLineShopper && mesh.userData.badgeContainer) {
    const badgeContainer = mesh.userData.badgeContainer as THREE.Object3D;
    badgeContainer.position.copy(newPosition);
    badgeContainer.position.y = 0.025; // Keep slightly above shopper
  }
};
